import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  Link,
  useColorModeValue,
  HStack,
  Badge,
  Divider,
  List,
  ListItem,
} from "@chakra-ui/react";
import { Link as RouterLink, Navigate, useParams } from "react-router-dom";
import PageSeo from "../../../seo/PageSeo";
import { SITE_URL } from "../../../seo/siteConfig";
import {
  articleSchema,
  breadcrumbSchema,
} from "../../../seo/structuredData";
import {
  getArticleBySlug,
  getRelatedArticles,
} from "../../../content/blog/articles";

function renderSection(section, key, muted) {
  if (section.type === "h2") {
    return (
      <Heading key={key} as="h2" size="md" mt={6} mb={2}>
        {section.text}
      </Heading>
    );
  }
  if (section.type === "ul") {
    return (
      <List key={key} spacing={2} pl={4} styleType="disc" color={muted}>
        {section.items.map((item, i) => (
          <ListItem key={i} lineHeight="tall">
            {item}
          </ListItem>
        ))}
      </List>
    );
  }
  return (
    <Text key={key} color={muted} lineHeight="tall" fontSize="md">
      {section.text}
    </Text>
  );
}

const BlogPost = () => {
  const { slug } = useParams();
  const post = getArticleBySlug(slug);
  const muted = useColorModeValue("gray.600", "gray.300");
  const deep = useColorModeValue("gray.900", "white");
  const cardBg = useColorModeValue("whiteAlpha.900", "gray.800");

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const url = `${SITE_URL}/blog/${post.slug}`;
  const related = getRelatedArticles(post);
  const jsonLd = [
    articleSchema({
      title: post.title,
      description: post.metaDescription,
      url,
      datePublished: post.publishedAt,
    }),
    breadcrumbSchema([
      { name: "Home", url: SITE_URL },
      { name: "Blog", url: `${SITE_URL}/blog` },
      { name: post.title, url },
    ]),
  ];

  return (
    <Box as="article" bg="transparent" py={{ base: 8, md: 12 }} minH="70vh">
      <PageSeo
        title={post.title}
        description={post.metaDescription}
        keywords={post.keywords}
        canonicalPath={`/blog/${post.slug}`}
        ogType="article"
        jsonLd={jsonLd}
      />
      <Container maxW="container.md" px={{ base: 4, md: 6 }}>
        <VStack align="stretch" spacing={6}>
          <Box as="nav" aria-label="Breadcrumb" fontSize="sm" color={muted}>
            <Link as={RouterLink} to="/" color="teal.500">
              Home
            </Link>
            {" / "}
            <Link as={RouterLink} to="/blog" color="teal.500">
              Blog
            </Link>
            {" / "}
            <Text as="span">{post.title}</Text>
          </Box>

          <Box
            bg={cardBg}
            borderRadius="2xl"
            borderWidth="1px"
            p={{ base: 6, md: 8 }}
            boxShadow="md"
          >
            <HStack spacing={2} flexWrap="wrap" mb={3}>
              <Badge colorScheme="teal">{post.category}</Badge>
              <Text fontSize="xs" color={muted}>
                {post.publishedAt} · {post.readMinutes} min read
              </Text>
            </HStack>
            <Heading as="h1" size="xl" color={deep} mb={4} lineHeight="shorter">
              {post.title}
            </Heading>
            <Text fontSize="lg" color={muted} mb={6} lineHeight="tall">
              {post.metaDescription}
            </Text>
            <Divider mb={4} />
            {post.sections.map((s, i) => renderSection(s, i, muted))}
          </Box>

          <Box
            bg={cardBg}
            borderRadius="xl"
            borderWidth="1px"
            p={6}
            textAlign="center"
          >
            <Heading as="h2" size="sm" mb={2}>
              Try Manage360 free
            </Heading>
            <Text fontSize="sm" color={muted} mb={4}>
              Screen AI, Media AI, money, projects, todos, and chores in one
              workspace—AI credits optional.
            </Text>
            <Button as={RouterLink} to="/register" colorScheme="teal" size="lg">
              Create free account
            </Button>
          </Box>

          {related.length > 0 ? (
            <Box>
              <Heading as="h2" size="md" mb={3}>
                Related guides
              </Heading>
              <VStack align="stretch" spacing={2}>
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    as={RouterLink}
                    to={`/blog/${r.slug}`}
                    color="teal.500"
                    fontWeight="medium"
                  >
                    {r.title}
                  </Link>
                ))}
              </VStack>
            </Box>
          ) : null}
        </VStack>
      </Container>
    </Box>
  );
};

export default BlogPost;
